import React, { useCallback, useEffect, useRef, useState } from "react";

const ROUNDS = [3, 4, 5];
const randSeq = (n) => Array.from({ length: n }, () => Math.floor(Math.random() * 9));

export const SignalGame = ({ onWin, onPenalty }) => {
  const [round, setRound] = useState(0);
  const [seq, setSeq] = useState(() => randSeq(ROUNDS[0]));
  const [phase, setPhase] = useState("show");
  const [lit, setLit] = useState(null);
  const [input, setInput] = useState(0);
  const [error, setError] = useState(false);
  const timers = useRef([]);

  const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));
  const clearAll = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  const play = useCallback((s) => {
    setPhase("show");
    setInput(0);
    s.forEach((cell, k) => {
      later(() => setLit(cell), 700 + k * 650);
      later(() => setLit(null), 700 + k * 650 + 420);
    });
    later(() => setPhase("input"), 700 + s.length * 650);
  }, []);

  useEffect(() => {
    play(seq);
    return clearAll;
  }, [seq, play]);

  const tap = (cell) => {
    if (phase !== "input") return;
    setLit(cell);
    later(() => setLit(null), 180);
    if (cell !== seq[input]) {
      setError(true);
      onPenalty(4);
      clearAll();
      setPhase("show");
      later(() => setLit(null), 350);
      later(() => {
        setError(false);
        play(seq);
      }, 700);
      return;
    }
    if (input + 1 < seq.length) {
      setInput(input + 1);
      return;
    }
    setPhase("show");
    if (round + 1 >= ROUNDS.length) {
      onWin();
      return;
    }
    later(() => {
      setRound(round + 1);
      setSeq(randSeq(ROUNDS[round + 1]));
    }, 600);
  };

  return (
    <div className="space-y-5" data-testid="signal-game">
      <div className="flex items-center justify-between font-tac text-sm">
        <span data-testid="signal-round" className="text-slate-400">ROUND {round + 1} / {ROUNDS.length}</span>
        <span data-testid="signal-phase" data-phase={phase} className={phase === "input" ? "text-blue-300" : "text-slate-500"}>
          {error ? "WRONG SIGNAL — REPLAYING" : phase === "input" ? `YOUR TURN  ${input}/${seq.length}` : "WATCH THE LIGHTS"}
        </span>
      </div>
      <div data-testid="signal-sequence" data-seq={seq.join(",")} className={`grid grid-cols-3 gap-3 max-w-xs mx-auto ${error ? "spore-shake" : ""}`}>
        {Array.from({ length: 9 }).map((_, i) => (
          <button
            key={i}
            type="button"
            data-testid={`signal-cell-${i}`}
            onClick={() => tap(i)}
            disabled={phase !== "input"}
            className={`aspect-square rounded-sm border transition-[background-color,box-shadow,border-color] duration-150 ${
              lit === i
                ? error
                  ? "bg-red-500 border-red-300 shadow-[0_0_25px_rgba(255,46,99,0.7)]"
                  : "bg-blue-400 border-blue-200 shadow-[0_0_25px_rgba(96,165,250,0.8)]"
                : "bg-slate-900 border-slate-700 hover:border-blue-500/60"
            }`}
          />
        ))}
      </div>
    </div>
  );
};
